import { EditingTeamRole, type GetModernizedCourseEditingTeamQuery } from "@axio/graphql/generated/graphql";

type EditingTeamMember = GetModernizedCourseEditingTeamQuery["getModernizedCourseEditingTeam"]["members"][number];

export type CourseModernizerReviewRoleStatus = "loading" | "known" | "unknown";

export interface CourseModernizerReviewPermissions {
  role: EditingTeamRole | null;
  roleStatus: CourseModernizerReviewRoleStatus;
  isArchitect: boolean;
  isObserver: boolean;
  canEdit: boolean;
  canComment: boolean;
  canQueue: boolean;
  canPublish: boolean;
  canManageTeam: boolean;
  canRestoreVersion: boolean;
  showViewOnlyBadge: boolean;
  showPublishControl: boolean;
}

const REVIEW_WRITE_ROLES = new Set<EditingTeamRole>([
  EditingTeamRole.Architect,
  EditingTeamRole.Instructor,
  EditingTeamRole.InstructionalDesigner,
]);

export function getCurrentUserEditingTeamRole(
  members: readonly EditingTeamMember[] | null | undefined,
  currentUserId: string | null,
  architectId?: string | null
): EditingTeamRole | null {
  if (!currentUserId) {
    return null;
  }

  if (architectId === currentUserId) {
    return EditingTeamRole.Architect;
  }

  if (!members) {
    return null;
  }

  return members.find((member) => member.userId === currentUserId)?.role ?? null;
}

export function buildCourseModernizerReviewPermissions({
  architectId,
  currentUserId,
  isLoading,
  members,
}: {
  architectId?: string | null;
  currentUserId: string | null;
  isLoading: boolean;
  members: readonly EditingTeamMember[] | null | undefined;
}): CourseModernizerReviewPermissions {
  const role = getCurrentUserEditingTeamRole(members, currentUserId, architectId);
  const roleStatus: CourseModernizerReviewRoleStatus = role ? "known" : isLoading ? "loading" : "unknown";
  const isArchitect = role === EditingTeamRole.Architect;
  const isObserver = role === EditingTeamRole.Observer;
  const canWrite = role ? REVIEW_WRITE_ROLES.has(role) : false;

  return {
    role,
    roleStatus,
    isArchitect,
    isObserver,
    canEdit: canWrite,
    canComment: canWrite,
    canQueue: canWrite,
    canPublish: isArchitect,
    canManageTeam: isArchitect,
    canRestoreVersion: isArchitect,
    showViewOnlyBadge: isObserver,
    showPublishControl: isArchitect || isObserver,
  };
}
